import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Link,
  MessagePlugin,
  Popconfirm,
  Space,
  Table,
  Upload,
  Form,
  Input,
} from 'tdesign-react';
import styled from 'styled-components';
import { useMemoizedFn } from 'ahooks';
import { isEmpty } from 'lodash';
import { DownloadIcon, UploadIcon } from 'tdesign-icons-react';
import useTable from '@/hooks/useTable';
import {
  deleteVariable,
  exportVariables,
  getVariableList,
  uploadFile,
} from '@/services/variableService';
import {
  scopeTypeOptions,
  showTypeOptions,
  variableLevelOptions,
  variableOptionalOptions,
} from '@/utils/constant';
import { isVoid, renderOptions } from '@/utils/common';
import NoData from '@/components/NoData';
import PermWrapper from '@/components/PermWrapper';
import BuriedPermWrapper from '@/components/BuriedWrapper/BuriedPermWrapper';
import Modal from './Modal';

const { FormItem } = Form;

export default function VariableTable({ group, scopeType }) {
  const [detail, setDetail] = useState(false);
  const [visible, setVisible] = useState(false);
  const [dependOptions, setDependOptions] = useState([]); // 依赖
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const EVENTS = useMemo(() => {
    switch (scopeType) {
      // 主机变量
      case 0:
        return {
          CREATE: 'TEMPLATE_DETAIL_VARIABLE_HOST_CREATE', // 新增变量
          CREATE_SUBMIT: 'TEMPLATE_DETAIL_VARIABLE_HOST_CREATE_SUBMIT', // 新增变量确认
        };
      // 容器变量
      case 1:
        return {
          CREATE: 'TEMPLATE_DETAIL_VARIABLE_CONTAINER_CREATE',
          CREATE_SUBMIT: 'TEMPLATE_DETAIL_VARIABLE_CONTAINER_CREATE_SUBMIT',
        };
      default:
        return;
    }
  }, [scopeType]);

  const columns = [
    {
      colKey: 'row-select',
      type: 'multiple',
      fixed: 'left',
      width: 46,
    },
    {
      title: '变量key',
      colKey: 'key',
      fixed: 'left',
      width: 140,
    },
    {
      title: '展示名称',
      colKey: 'keyName',
      width: 160,
    },
    {
      title: '变量域',
      colKey: 'scopeType',
      options: scopeTypeOptions,
      cell: renderOptions,
      width: 130,
    },
    {
      title: '变量组',
      colKey: 'groupName',
      width: 130,
    },
    {
      title: '变量范围',
      colKey: 'variableLevel',
      options: variableLevelOptions,
      cell: renderOptions,
      width: 140,
    },
    {
      title: '变量类型',
      colKey: 'variableType',
      options: variableOptionalOptions,
      cell: renderOptions,
      width: 140,
    },
    {
      title: '展现形式',
      colKey: 'showType',
      options: showTypeOptions,
      cell: renderOptions,
      width: 140,
    },
    {
      title: '变量依赖',
      colKey: 'dependKey',
      cell: ({ row }) => (row.dependKey ? `${row.dependKey} = ${row.dependValue}` : '-'),
      width: 220,
    },
    {
      title: '脚本/组件模板引用数',
      colKey: 'stencilCount',
      width: 180,
    },
    {
      title: '备注说明',
      colKey: 'desc',
      ellipsis: true,
      width: 200,
    },
    {
      colKey: 'option',
      title: '操作',
      align: 'center',
      fixed: 'right',
      cell: ({ row }) => (
        <Space>
          <PermWrapper resource='pulsar:stencil:variables:variablesUpdate'>
            <Link
              theme='primary'
              hover='color'
              onClick={() => {
                setDetail(row);
                setVisible(true);
              }}
            >
              修改
            </Link>
          </PermWrapper>
          <PermWrapper resource='pulsar:stencil:variables:variablesDelete'>
            <Popconfirm theme='danger' content='确定删除？' onConfirm={() => handleDelete(row)}>
              <Link theme='danger' hover='color'>
                删除
              </Link>
            </Popconfirm>
          </PermWrapper>
        </Space>
      ),
      width: 120,
    },
  ];

  const loadData = useMemoizedFn(async (params) => {
    const { key = '' } = params || {};
    if (group && group.id) {
      const { data } = await getVariableList({ groupId: group.id, scopeType });
      setData(data.filter((item) => item.key.indexOf(key) > -1));
      // 初始化可依赖，剔除自身，剔除已有依赖,只支持集群变量
      setDependOptions(
        data
          .filter((item) => isVoid(item.dependId) && item.variableLevel === 0)
          .map((item) => ({
            ...item,
            label: item.key,
            value: item.id,
          })),
      );
    }
  });

  const addNew = () => setVisible(true);

  const doUpload = async (file) => {
    if (file[0]) {
      const formData = new FormData();
      formData.append('file', file[0]);
      let uploadMsg;
      try {
        uploadMsg = MessagePlugin.loading({
          duration: 0,
          content: '正在上传...',
        });
        await uploadFile(formData);
        MessagePlugin.success('上传成功！');
        setSelectedRowKeys([]);
        doRefresh();
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        MessagePlugin.close(uploadMsg);
      }
    }
  };

  const { config, header, setData, doSearch, doRefresh } = useTable({
    columns,
    onSearch: loadData,
    search: (
      <FormItem name='key'>
        <Input clearable placeholder='变量key' />
      </FormItem>
    ),
    operators: (
      <Space size='small'>
        <PermWrapper resource='pulsar:stencil:variables:variablesImport'>
          <Upload theme='file' onSelectChange={doUpload}>
            <Button variant='outline' icon={<DownloadIcon />}>
              导入
            </Button>
          </Upload>
        </PermWrapper>

        {!isEmpty(selectedRowKeys) && (
          <PermWrapper resource='pulsar:stencil:variables:variablesExport'>
            <Button
              onClick={() => exportVariables(selectedRowKeys)}
              variant='outline'
              icon={<UploadIcon />}
            >
              导出
            </Button>
          </PermWrapper>
        )}

        <BuriedPermWrapper
          resource='pulsar:stencil:variables:variablesCreate'
          eventId={EVENTS.CREATE}
        >
          <Button theme='primary' onClick={addNew}>
            添加变量
          </Button>
        </BuriedPermWrapper>
      </Space>
    ),
  });

  useEffect(() => {
    doSearch();
  }, [doSearch, group, scopeType]);

  const handleDelete = async (row) => {
    await deleteVariable(row.id);
    MessagePlugin.success('操作成功');
    doRefresh();
  };

  const handleClose = (refresh) => {
    setVisible(false);
    setDetail(null);
    if (refresh) {
      doRefresh();
    }
  };

  return isEmpty(group) ? (
    <NoData
      tip={
        <Space align='center' size='small' style={{ marginLeft: -36 }}>
          <div style={{ whiteSpace: 'nowrap' }}>请现在左侧，新建变量组</div>
          <PermWrapper resource='pulsar:stencil:variables:variablesImport'>
            <Space align='center' size='small'>
              或
              <Upload theme='file' onSelectChange={doUpload}>
                <Button variant='text' size='small' theme='primary' icon={<DownloadIcon />}>
                  导入
                </Button>
              </Upload>
            </Space>
          </PermWrapper>
        </Space>
      }
      style={{ flex: 1, padding: 30 }}
    />
  ) : (
    <Root>
      {header}
      <Table {...config} selectedRowKeys={selectedRowKeys} onSelectChange={setSelectedRowKeys} />
      <Modal
        group={group}
        visible={visible}
        id={detail?.id}
        scopeType={scopeType}
        dependOptions={dependOptions}
        handleClose={handleClose}
        eventId={EVENTS.CREATE_SUBMIT}
      />
    </Root>
  );
}

const Root = styled.div`
  flex: 1;
  padding: 24px;
  overflow-y: auto;
`;
